import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import {
  IS_EMIT_VALUES,
  RAISE_ERROR_ON_EMPTY_INFORM_ID,
  SOCKET_URL,
} from '../constants/settings';
import { I18n } from 'i18n-js';
import EventEmitter from 'eventemitter3';
import { useDispatch, useSelector } from 'react-redux';
import { initializeGrafana } from 'src/layouts/layout-components/Grafana';
import { fetchLogout } from '../redux/auth/action';
import { localesSelectors } from '../redux/locales/selectors';
import { getCookie } from '@utils/getCookie/getCookie';
import { initialGrafana } from '@redux/settings/action';

const i18n = new I18n();
export const EE = new EventEmitter();

const AppContext = createContext({
  socket: null,
  isHighPing: false,
  isBrowserTabChanged: false,
  isRouterChanged: false,
  isConnected: false,
  lang: 'en_US',
  setLang: () => {},
  setUser: () => {},
  user: {},
  translations: {
    select_value: 'Select value',
    missing_data: 'Missing data',
    no_data: 'No data',
    adding_data_process: 'Adding data processing...',
    attribute_marker_found_warning: '',
    found: '',
    searching: ''
  },
  i18n,
  appId: undefined,
});

export const AppContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const appId = getCookie('appId');
  const [socket, setSocket] = useState(null);
  /**
   * Текущее состояние ws-соединения. Стартует с `false`: до хендшейка
   * соединения нет, и прежний `true` по умолчанию врал потребителям.
   */
  const [isConnected, setIsConnected] = useState(false);
  const [isHighPing, setIsHighPing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [cachedInterfaceLanguage, setCachedInterfaceLanguage] = useState(
    localStorage.getItem('activeLanguage'),
  );
  const [sideBarRoutes, setSideBarRoutes] = useState([]);
  const [isBrowserTabChanged, setIsBrowserTabChanged] = useState(false);
  const pingLimit = parseInt(process.env.REACT_APP_WS_PING, 10);
  const [dir, setDir] = useState('ltr');
  const [user, setUser] = useState({});
  const socketRef = useRef(null);
  const { translations } = i18n;
  // язык контента
  const lang = useSelector(localesSelectors.getSelectedLocale);
  const settings = useSelector((state) => state?.settingsReducer);

  const pingDetector = (msg) => {
    setIsHighPing(msg.ping <= pingLimit);
  };

  const setListeners = () => {
    socket.on('ping', pingDetector);
    socket.on('system', (msg) => {
      if (msg?.action === 'logout') {
        dispatch(fetchLogout());
        window.location = '/';
      }
    });
  };

  useEffect(() => {
    if (settings?.data?.system?.common && !settings.grafana) {
      try {
        if (!settings?.data?.system?.common?.isDev) {
          initializeGrafana();
          dispatch(initialGrafana());
        }
      } catch (e) {
        console.error('Grafana connection error:', e);
      }
    }
  }, [settings?.data?.system?.common]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    if (!socket && user.accessToken) {
      const socketIo = io(SOCKET_URL, {
        transports: ['websocket'],
        auth: {
          token: user.accessToken,
          userId: user.id,
        },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 500,
      });
      setSocket(socketIo);
    }
    if (
      localStorage.getItem('activeLanguage') === 'ae_AE' ||
      localStorage.getItem('activeLanguage') === 'he_HE'
    ) {
      setDir('rtl');
    }
    if (localStorage.getItem('activeLanguage') === 'ar_AR') {
      setDir('rtl');
    }
    return () => {
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [user]);

  useEffect(() => {
    if (socket) {
      // проверка пинга
      setInterval(() => {
        const start = Date.now();
        socket.volatile.emit('ping', { time: start });
      }, 60 * 1000);
      setListeners();
      // проверка индексации товаров
      socket.emit('checkIndexProducts');
      // отслеживаем соединение
      socket.on('connect', () => {
        setIsConnected(true);
      });
      socket.on('disconnect', () => {
        setIsConnected(false);
      });
      socket.on('connect_error', () => {
        setIsConnected(false);
      });
      // подстраховка: сокет мог успеть подключиться до навешивания слушателей
      setIsConnected(socket.connected);
      socketRef.current = socket;
    }
  }, [socket]);

  useEffect(() => {
    document.addEventListener('visibilitychange', () => {
      setIsBrowserTabChanged(document.visibilityState !== 'visible');
    });
    if (IS_EMIT_VALUES) {
      EE.on('keyup', (data) => {
        const { id, value, lang, appId } = data;
        if (socketRef.current && id) {
          socketRef.current.emit('attributeChangingValue', {
            id,
            value,
            lang,
            date: new Date(),
            appId,
          });
        }
        // @todo: @deprecated soon
        if (!id && RAISE_ERROR_ON_EMPTY_INFORM_ID) {
          console.error('InformId пустой', data);
        }
      });
    }
    return () => {
      document.removeEventListener('visibilitychange', null);
      if (IS_EMIT_VALUES) {
        EE.removeAllListeners();
      }
    };
  }, []);

  /**
   * Значение контекста мемоизируется: без этого объект пересоздавался на каждый
   * рендер провайдера и заставлял перерисовываться ВСЕХ потребителей useApp
   * (Header, Tooltip, страницы редактирования). Любой локальный цикл
   * "эффект → setState" превращался при этом в перерисовку всего дерева и
   * падал с React error #185 (Maximum update depth exceeded) в случайном месте.
   */
  const contextValue = useMemo(() => ({
    socket,
    isHighPing,
    isBrowserTabChanged,
    isConnected,
    lang,
    // setLang,
    setLoading,
    loading,
    i18n,
    translations,
    dir,
    setDir,
    cachedInterfaceLanguage,
    setCachedInterfaceLanguage,
    sideBarRoutes,
    setSideBarRoutes,
    appId,
    setUser,
    user,
  }), [
    socket,
    isHighPing,
    isBrowserTabChanged,
    isConnected,
    lang,
    loading,
    translations,
    dir,
    cachedInterfaceLanguage,
    sideBarRoutes,
    appId,
    user,
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  return useContext(AppContext);
};
